(function () {
  'use strict';

  if (window.DieuxAnchorScroll) return;

  function maxScroll() {
    const doc = document.documentElement;
    return Math.max(0, doc.scrollHeight - doc.clientHeight);
  }

  /**
   * The anchor id a fragment names.
   *
   * @param {string} hash - Fragment, including the leading `#`.
   * @returns {string} Decoded id, or an empty string when the fragment names nothing.
   */
  function anchorId(hash) {
    if (!hash || hash.length < 2) return '';
    try {
      return decodeURIComponent(hash.slice(1));
    } catch {
      return hash.slice(1);
    }
  }

  /**
   * The element an anchor id scrolls to.
   *
   * @param {string} id - Anchor id, without the `#`.
   * @returns {Element|null} Element to scroll to, or `null` when the id names nothing scrollable.
   */
  function scrollTarget(id) {
    const target = id ? document.getElementById(id) : null;
    if (!target || !target.isConnected) return null;

    // A fragment naming a dialog opens it; `assets/dialog.js` owns that.
    if (target.localName === 'dialog') return null;

    return target;
  }

  /**
   * The element the page lands on for a target: the nearest ancestor its `data-anchor-scroll-closest` selector names, or the target itself.
   *
   * @param {Element} target - Element an anchor id names.
   * @returns {Element} Element whose scroll-margin line the page rests on.
   */
  function landingElement(target) {
    const selector = target.dataset?.anchorScrollClosest;
    return (selector && target.closest(selector)) || target;
  }

  /**
   * Asks whatever keeps a target hidden, such as a closed tab, to show it before it is measured.
   *
   * @param {Element} target - Element an anchor id names.
   * @param {boolean} animated - Whether the scroll that follows eases.
   * @returns {void}
   */
  function reveal(target, animated) {
    target.dispatchEvent?.(new CustomEvent('dieux:anchor:reveal', { bubbles: true, detail: { animated } }));
  }

  /**
   * Where the page must sit for an anchor to rest on its own scroll-margin line.
   *
   * @param {string} id - Anchor id, without the `#`.
   * @returns {number|null} Scroll offset in pixels, or `null` when the anchor is not in the document.
   */
  function resolveOffset(id) {
    const target = scrollTarget(id);
    if (!target) return null;

    const landing = landingElement(target);
    const band = Number.parseFloat(getComputedStyle(landing).scrollMarginBlockStart) || 0;
    const top = landing.getBoundingClientRect().top + window.scrollY - band;
    return Math.max(0, Math.min(maxScroll(), top));
  }

  /**
   * The same-document anchor a link names.
   *
   * @param {Element} link - Candidate anchor element.
   * @returns {string|null} Anchor id, or `null` when the link leaves this document or names no target.
   */
  function targetId(link) {
    if (!(link instanceof HTMLAnchorElement)) return null;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (url.search !== window.location.search) return null;

    const id = anchorId(url.hash);
    return scrollTarget(id) ? id : null;
  }

  const SETTLE_FRAMES = 8;
  const MAX_DURATION_MS = 8000;
  const DURATION_MS = 600;
  const ARRIVAL_EPSILON_PX = 1;
  const GOAL_EPSILON_PX = 0.5;
  const SCROLL_KEYS = new Set([
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    ' ',
    'Spacebar',
  ]);

  let run = null;

  /**
   * Deferred placeholders that still sit above a scroll offset, and so can still move it.
   *
   * @param {number} offset - Scroll offset to count above.
   * @returns {number} Pending placeholders above `offset`.
   */
  function pendingAbove(offset) {
    let count = 0;
    for (const element of document.querySelectorAll('defer-section')) {
      if (element.getBoundingClientRect().top + window.scrollY < offset) count += 1;
    }
    return count;
  }

  /**
   * @returns {boolean} Whether a tracked scroll is still running.
   */
  function isSettling() {
    return run !== null;
  }

  function interrupt(event) {
    if (event.type === 'keydown' && !SCROLL_KEYS.has(event.key)) return;
    cancel();
  }

  function bindInterrupts(bind) {
    const method = bind ? 'addEventListener' : 'removeEventListener';
    window[method]('wheel', interrupt, { passive: true });
    window[method]('touchmove', interrupt, { passive: true });
    window[method]('keydown', interrupt);
  }

  /**
   * Stops the tracked scroll and hands the page back.
   *
   * @returns {void}
   */
  function cancel(arrived = false) {
    if (!run) return;

    window.cancelAnimationFrame(run.frame);
    bindInterrupts(false);
    if (run.restoration) window.history.scrollRestoration = run.restoration;
    const { settle } = run;
    run = null;
    settle?.(arrived);
  }

  const INTERACTIVE =
    'a[href], area[href], button, input, select, textarea, iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

  /**
   * Moves focus to an element without scrolling; a non-interactive landing never wears a ring.
   *
   * @param {Element} element - Control or landing region to focus.
   * @returns {void}
   */
  function focusLanding(element) {
    if (typeof element?.focus !== 'function' || typeof element.matches !== 'function') return;

    if (element.matches(INTERACTIVE)) {
      element.focus({ preventScroll: true });
      return;
    }

    const addedTabindex = !element.hasAttribute?.('tabindex');
    if (addedTabindex) element.setAttribute('tabindex', '-1');
    element.setAttribute('data-anchor-landing', '');
    element.addEventListener(
      'blur',
      () => {
        element.removeAttribute('data-anchor-landing');
        if (addedTabindex) element.removeAttribute('tabindex');
      },
      { once: true }
    );
    element.focus({ preventScroll: true, focusVisible: false });
  }

  /**
   * Restores the focus the browser set at navigation time and a deferred swap then discarded.
   *
   * @param {string} id - Anchor that was scrolled to.
   * @returns {void}
   */
  function land(id) {
    const target = document.getElementById(id);
    if (!target || target.contains(document.activeElement)) return;

    focusLanding(target);
  }

  function step(now) {
    if (!run) return;

    const resolved = run.resolve();
    const goal = resolved === null ? run.goal : Math.max(0, Math.min(maxScroll(), resolved));
    if (Math.abs(goal - run.goal) < GOAL_EPSILON_PX && document.readyState === 'complete') {
      run.stable += 1;
    } else {
      run.stable = 0;
      run.goal = goal;
    }

    const elapsed = now - run.start;
    const progress = run.animated ? Math.min(1, elapsed / DURATION_MS) : 1;
    const eased = 1 - (1 - progress) ** 3;
    window.scrollTo({ top: run.from + (goal - run.from) * eased, behavior: 'instant' });

    const arrived = Math.abs(window.scrollY - goal) <= ARRIVAL_EPSILON_PX;
    const settled = run.id
      ? run.stable >= SETTLE_FRAMES && pendingAbove(goal) === 0
      : progress === 1;
    if ((arrived && settled) || elapsed >= MAX_DURATION_MS) {
      window.scrollTo({ top: goal, behavior: 'instant' });
      if (run.id) land(run.id);
      cancel(true);
      return;
    }

    run.frame = window.requestAnimationFrame(step);
  }

  /**
   * Scrolls to an anchor and holds it there while the document settles.
   *
   * @param {string} id - Anchor id, without the `#`.
   * @param {object} [options] - Scroll options.
   * @param {boolean} [options.animated] - Whether to ease. Defaults to the visitor's motion preference.
   * @returns {void}
   */
  function to(id, options) {
    const target = scrollTarget(id);
    if (!target) return;

    const reduced = window.DieuxFrame?.prefersReducedMotion() ?? false;
    const animated = options?.animated ?? !reduced;
    reveal(target, animated);

    const goal = resolveOffset(id);
    if (goal === null) return;

    cancel();
    window.DieuxDeferSection?.resolveAt(id, goal);

    run = {
      id,
      resolve: () => resolveOffset(id),
      goal,
      from: window.scrollY,
      start: performance.now(),
      stable: 0,
      animated,
      restoration: null,
      frame: 0,
      settle: null,
    };

    bindInterrupts(true);
    run.frame = window.requestAnimationFrame(step);
  }

  /**
   * Eases the page to an offset its resolver re-reads every frame, without moving focus.
   *
   * @param {() => (number|null)} resolve - Returns the scroll offset to land on, or `null` to keep the last one.
   * @param {object} [options] - Scroll options.
   * @param {boolean} [options.animated] - Whether to ease. Defaults to the visitor's motion preference.
   * @returns {Promise<boolean>} `true` once the page arrives, `false` when the visitor or another scroll interrupts it.
   */
  function toOffset(resolve, options) {
    const first = resolve();
    if (first === null) return Promise.resolve(false);

    const reduced = window.DieuxFrame?.prefersReducedMotion() ?? false;
    const animated = options?.animated ?? !reduced;

    cancel();

    return new Promise((settle) => {
      run = {
        id: null,
        resolve,
        goal: Math.max(0, Math.min(maxScroll(), first)),
        from: window.scrollY,
        start: performance.now(),
        stable: 0,
        animated,
        restoration: null,
        frame: 0,
        settle,
      };

      bindInterrupts(true);
      run.frame = window.requestAnimationFrame(step);
    });
  }

  /**
   * Whether an element sits in a fixed or sticky layer that scrolling the document cannot move.
   *
   * @param {Element} element - Element to test.
   * @returns {boolean} Whether the element or an ancestor below `body` is pinned.
   */
  function inPinnedLayer(element) {
    for (let node = element; node && node !== document.body; node = node.parentElement) {
      const { position } = getComputedStyle(node);
      if (position === 'fixed' || position === 'sticky') return true;
    }
    return false;
  }

  const RING_OWNERS = '.field__input, .g-field-edge';

  /**
   * The element that paints a focused control's ring: the field wrapper that outlines through `:has(:focus-visible)`, or the control itself.
   *
   * @param {Element} target - Focused element.
   * @returns {Element} Element whose box carries the ring.
   */
  function ringOwner(target) {
    for (let node = target.closest(RING_OWNERS); node; node = node.parentElement?.closest(RING_OWNERS) ?? null) {
      if (node.parentElement?.closest('.g-field-composed')) continue;
      if (getComputedStyle(node).outlineStyle !== 'none') return node;
    }
    return target;
  }

  /**
   * How far a ring's outer edge reaches past its owner's box.
   *
   * @param {Element} owner - Element carrying the ring.
   * @returns {number} Outward reach in pixels, never negative.
   */
  function ringReach(owner) {
    const style = getComputedStyle(owner);
    if (style.outlineStyle === 'none') return 0;
    const width = Number.parseFloat(style.outlineWidth) || 0;
    const offset = Number.parseFloat(style.outlineOffset) || 0;
    return Math.max(0, width + offset);
  }

  /**
   * Scrolls a keyboard-focused element out from under the sticky header, using the document's keyboard scroll-padding as the clearance.
   *
   * @param {FocusEvent} event - `focusin` event.
   * @returns {void}
   */
  function keepKeyboardFocusClear(event) {
    if (document.documentElement.dataset.focusModality !== 'keyboard') return;
    if (isSettling()) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target === document.documentElement || target === document.body) return;
    if (target.id === 'MainContent' || target.hasAttribute('data-anchor-landing')) return;
    if (target.closest('dialog, dieux-header, [popover]')) return;

    const frame = window.DieuxFrame;
    if (!frame) return;

    frame.read(() => {
      if (!target.isConnected || document.activeElement !== target || isSettling()) return;
      if (inPinnedLayer(target)) return;

      const clearance = Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingBlockStart) || 0;
      if (clearance <= 0) return;

      const rect = target.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;

      const bar = document.querySelector(".sticky-add-to-cart[data-sticky-state='visible'] .sticky-add-to-cart__bar");
      const barTop = bar ? bar.getBoundingClientRect().top : window.innerHeight;
      if (bar && rect.bottom > barTop) return;

      const owner = ringOwner(target);
      const reach = ringReach(owner);
      const box = owner.getBoundingClientRect();
      const top = box.top - reach;
      const bottom = box.bottom + reach;
      if (bottom - top > barTop - clearance) return;

      let delta = 0;
      if (top < clearance) delta = top - clearance;
      else if (bar && bottom > barTop) delta = bottom - barTop;
      if (delta === 0) return;

      frame.write(() => window.scrollBy({ top: delta, behavior: 'instant' }));
    });
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest?.('a[href*="#"]');
    if (!link) return;

    const id = targetId(link);
    if (id) to(id);
  });

  document.addEventListener('focusin', keepKeyboardFocusClear);

  window.addEventListener('hashchange', () => {
    const id = anchorId(window.location.hash);
    if (scrollTarget(id)) to(id);
  });

  function followInitialFragment() {
    const id = anchorId(window.location.hash);
    if (!scrollTarget(id)) return;

    to(id, { animated: false });

    const { history } = window;
    if (!run || !history || !('scrollRestoration' in history)) return;
    run.restoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', followInitialFragment);
  } else {
    followInitialFragment();
  }

  window.DieuxAnchorScroll = Object.freeze({
    anchorId,
    scrollTarget,
    resolveOffset,
    targetId,
    pendingAbove,
    isSettling,
    to,
    toOffset,
    cancel,
    focusLanding,
    SETTLE_FRAMES,
    MAX_DURATION_MS,
  });
})();
